import { makeAutoObservable, ObservableMap, runInAction } from "mobx";
import { createWorker } from "@ffmpeg/ffmpeg";
import * as AWS from "aws-sdk";
import MusicTrackState from "../MusicTrack/MusicTrackState";
import SearchState, {
  CLOUDFRONT_BASE_URL,
  API_BASE_URL,
  BASE_URL,
  DISABLE_CACHEING,
} from "../../SearchState";
import WaveformDisplay from "../MusicTrack/WaveformDisplay/WaveformDisplay";
import retryFetch from "../../utils/retryFetch";

/** @typedef {import("@shared/typedefs").Music} Music */
/** @typedef {import("@shared/authEndpointDatatypes").S3AuthGetResponse} S3AuthGetResponse */

export const SNAP_PERCENTAGE = 0.03;

export default class VideoSyncState {
  /** @type {string | null} */
  url;

  /** @type {string | null} */
  filename;

  /** @type {HTMLVideoElement} */
  videoRef;

  videoLoaded = false;

  videoDuration = 0;

  playing = false;

  exporting = false;

  /** @type {string} */
  exportText = "export synced video";

  exportProgress = 0.0;

  exportCount = 0;

  time = 0.0;

  percentage = 0.0;

  /** @type {ObservableMap<string, number>} */
  audioOffset = new ObservableMap();

  videoAudioLevel = 0.7;

  /** @type {SearchState} */
  searchState;

  /** @type {WaveformDisplay} */
  waveformRef;

  videoUploadProgress = 0;

  /** @type {string | null} */
  videoFile = null;

  /** @type {string} */
  videoUploadText = "Upload Video";

  showingVideoListing = false;

  /** @type {Array<any>} */
  videosInAccount = [];

  /** @type {Array<any>} */
  filteredVideosInAccount = [];

  /** @type {string} */
  filterSavedTerm = "";

  /** @type {number | null} */
  showingPanelFirst = -1;

  /** @type {boolean | null} */
  isBeingManuallyDragged = false;

  /**
   * @param {SearchState} searchState
   */
  constructor(searchState) {
    makeAutoObservable(this, {}, { enforceActions: "observed" });
    this.searchState = searchState;
  }

  /**
   * @param {WaveformDisplay} waveform
   */
  setWaveformRef = (waveform) => {
    this.waveformRef = waveform;
  };

  /**
   * Handles screen click - loads new video if none is loaded
   * @action
   */
  clickScreen = () => {
    if (this.url == null) {
      this.loadNewVideo();
    }
  };

  /**
   * Toggles video playback between play and pause
   * @action
   */
  togglePause = () => {
    if (this.videoLoaded) {
      if (
        this.searchState.selectedTrack == null &&
        MusicTrackState.globallyActiveTrack == null
      ) {
        if (this.searchState.visibleMusic.length > 0) {
          this.searchState.visibleMusic[0].play();
        }
      } else {
        if (!this.searchState.timeline.playing) {
          this.searchState.timeline.play();
        } else {
          this.searchState.timeline.pause();
        }
      }
    }
  };

  /**
   * Resets video position to the beginning
   * @action
   */
  resetPosition = () => {
    this.searchState.timeline.setPosition(0);
  };

  hideVideoList = () => {
    this.showingVideoListing = false;
  };

  close = () => {
    this.filterVideos("");
    this.showingVideoListing = false;
  };

  resetSearchFilter = () => {
    this.showingPanelFirst = -1;
  };

  showSearchFilter = () => {
    this.showingPanelFirst = parseInt(this.showingPanelFirst, 2) + 1;
  };

  /**
   * @param {string} term
   */
  filterVideos = (term) => {
    if (term.length > 2) {
      this.filterSavedTerm = term;
    } else {
      this.filterSavedTerm = ``;
    }
    this.filterVideosBySearch();
  };

  filterVideosBySearch = () => {
    this.filteredVideosInAccount = [];
    if (this.filterSavedTerm !== ``) {
      const filteredVideos = this.videosInAccount.filter((video) => {
        const name = video.name ? video.name : ``;

        if (name.toLowerCase().includes(this.filterSavedTerm.toLowerCase())) {
          return video;
        }
        return false;
      });
      if (filteredVideos) this.filteredVideosInAccount = filteredVideos;
      return false;
    }
  };

  setVideosInAccount = (videos) => {
    this.videosInAccount = videos;
  };

  addVideoToPlaylist = (videoId) => {
    const savedVideo = {
      videoId,
      searchId: parseInt(this.searchState.searchId),
    };
    retryFetch(`${API_BASE_URL}/api/v1/video/add-to-playlist`, {
      method: "POST",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedVideo),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.success === true) {
          console.log("success");
        } else {
          alert("you have no videos uploaded into your account");
        }
      });
  };

  cleanVideoDetails = (video) => {
    video.url = video.encoded
      .replace(
        `s3://asvideotranscode`,
        `https://asvideotranscode.s3.amazonaws.com`,
      )
      .replaceAll("+", "%2B")
      .replaceAll(" ", "+");
    video.name = video.original;
    return video;
  };

  showVideoList = () => {
    retryFetch(`${API_BASE_URL}/api/v1/videos`, {
      method: "POST",
      headers: new Headers({ "content-type": "application/json" }),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.success === true) {
          const fetchedVideos = [];
          response.videos.forEach((video) => {
            const v = {};
            v.id = video.id;
            v.name = video.original;
            v.status = video.status;
            if (video.status) {
              v.url = video.encoded
                .replace(
                  `s3://asvideotranscode`,
                  `https://asvideotranscode.s3.amazonaws.com`,
                )
                .replaceAll("+", "%2B")
                .replaceAll(" ", "+");
            }

            // Format date as M/D/YYYY HH:mm UTC
            const date = new Date(video.created_on);
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            v.created_on = `${month}/${day}/${year} ${hours}:${minutes} UTC`;

            fetchedVideos.push(v);
          });

          this.setVideosInAccount(fetchedVideos);
          this.showingVideoListing = true;
          // console.log(this.videosInAccount);
        } else {
          alert("you have no videos uploaded into your account");
        }
      });
  };

  onFileChange = (event) => {
    console.log(event.target.files[0]);
    this.videoFile = event.target.files[0];
    console.log(this.videoFile);
    this.uploadNewVideo();
  };

  uploadNewVideo = () => {
    // Create a simple hash of the original filename for collision resistance
    const hash = this.videoFile.name
      .split('')
      .reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0)
      .toString(16)
      .slice(-4);

    // Sanitize filename for S3
    const baseName = this.videoFile.name
      .replace(/[&]/g, 'and')
      .replace(/\s+/g, '_')
      .replace(/[()]/g, '')
      .replace(/[^a-zA-Z0-9._-]/g, '');
    
    const sanitizedName = baseName.replace(/\.([^.]*?)$/, `_${hash}.$1`);

    return retryFetch(`${API_BASE_URL}/api/v1/auth/s3video_credentials`, {
      method: "GET",
      redirect: "error",
      withCredentials: true,
      credentials: "include",
      cache: DISABLE_CACHEING ? "reload" : "default",
    })
      .then((r) => r.json())
      .then((r) => {
        console.log(r);
        if (!r.authorized) {
          alert("You are not authorized to upload a video");
          return Promise.resolve();
        }

        AWS.config.update({
          region: r.bucketRegion,
          credentials: new AWS.CognitoIdentityCredentials({
            IdentityPoolId: r.identityPoolId,
          }),
        });

        const s3Params = {
          Bucket: "asvideotranscode",
          Key: `originals/${sanitizedName}`,
          Expires: 100000,
        };

        const s3 = new AWS.S3({
          apiVersion: "2006-03-01",
          params: s3Params,
        });

        this.uploading = true;
        /**
         * @param {File} video
         * @param {string} path
         * @param {string} contentName
         * @param {(progress: number) => void} progressCallback
         */
        const uploadVideoToS3 = (
          video,
          path,
          contentName,
          progressCallback,
        ) => {
          const upload = s3.upload(
            {
              Key: path,
              Body: video,
              // ACL: 'public-read',
              // Metadata: {
              //   'Content-Disposition': contentName
              // },
              ContentDisposition: contentName,
            },
            (err) => {
              if (err) {
                console.error(err);
                alert(
                  `There was an error uploading your video: ${err.message}`,
                );
              }
            },
          );
          upload.on("httpUploadProgress", (evt) => {
            progressCallback(evt.loaded / evt.total);
          });
          return upload.promise();
        };

        return Promise.all([
          uploadVideoToS3(
            this.videoFile,
            `originals/${sanitizedName}`,
            this.videoFile.name,
            (progress) => {
              this.videoUploadProgress = progress;
              const p = (progress * 100).toFixed();
              if (p < 100) {
                this.changeVideoUploadText(`Progress: ${p}%`);
              } else {
                this.changeVideoUploadText(`Complete`);
                setTimeout(() => {
                  this.changeVideoUploadText(`Upload Video`);
                }, "1000");
              }
            },
          ),
        ]).then(() => {
          this.insertVideoIntoDatabase(sanitizedName);
          console.log(`Successfully uploaded video file`);
        });
      });
  };

  insertVideoIntoDatabase = (filename) => {
    retryFetch(`${API_BASE_URL}/api/v1/insertvideo`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify({
        name: filename,
        size: this.videoFile.size,
      }),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((response) => response.json())
      .then((response) => {
        console.log(this.response);
      });
  };

  changeVideoUploadText = (text) => {
    this.videoUploadText = text;
  };

  loadNewVideo = () => {
    /** @type {HTMLInputElement} */
    const videoSelector = document.createElement("input");
    videoSelector.type = "file";
    videoSelector.style.display = "none";
    videoSelector.accept = "video/*,.mov,.mp4";
    if (document.body != null) {
      const body = document.body;
      body.appendChild(videoSelector);
      videoSelector.addEventListener("change", () => {
        this.resetVideo();
        const files = videoSelector.files;
        this.setVideo(URL.createObjectURL(files[0]), files[0].name);
        this.searchState.logEvent(`video-sync`, "loaded", `${files[0].name}`);
        body.removeChild(videoSelector);
      });
      videoSelector.click();
    }
  };

  /**
   * @param {number} progress
   */
  setExportProgress = (progress) => {
    this.exportProgress = progress;
  };

  /**
   * @param {string} status
   */
  setExportText = (status) => {
    this.exportText = status;
  };

  /**
   * @param {number} count
   */
  setExportCount = (count) => {
    this.exportCount = count;
  };

  /**
   * @param {boolean} status
   */
  setExportingStatus = (status) => {
    this.exporting = status;
  };

  downloadMixedVideo = async (event) => {
    if (this.exporting === true) return;
    this.setExportingStatus(true);

    // console.log('download mixdown')
    console.log("this.url", this.url);
    // console.log(this.filename)
    // console.log('music volume:', this.searchState.musicVolume)
    // console.log('video volume:', this.videoAudioLevel)
    console.log("audioOffset:", this.searchState.timeline.audioOffset);
    console.log("video duration:", this.videoDuration);
    // console.log(this.searchState.selectedTrack.audioArrayBuffer);
    console.log(
      "audio duration:",
      this.searchState.selectedTrack.audioBuffer.duration,
    );
    // console.log('this.searchState.timeline', this.searchState.timeline)
    // return;

    let totalMusicDuration =
      this.searchState.selectedTrack.audioBuffer.duration;
    const videoDuration = this.videoDuration;
    let cuePosition = this.searchState.timeline.position;
    const addToCuePosition = 0;
    let audioToRemove = 0;
    let audioToAdd = 0;
    const audioOffset = this.searchState.timeline.audioOffset;
    const cuePos = "";

    if (audioOffset < 0) {
      audioToRemove = Math.abs(audioOffset.toFixed(4));
      totalMusicDuration -= audioToRemove;
    } else if (audioOffset > 0) {
      audioToAdd = addToCuePosition + Math.abs(audioOffset.toFixed(4));
      totalMusicDuration += audioToAdd;
    }
    if (cuePosition > 0) {
      cuePosition = Math.abs(cuePosition.toFixed(4));

      // audioToRemove = audioToRemove + addToCuePosition
      totalMusicDuration -= cuePosition;

      if (cuePosition != 0) {
        // audioToAdd += cuePosition
        // cuePos = '-ss ' + cuePosition
      }
    }
    console.log("totalMusicDuration:", totalMusicDuration);
    console.log("audioOffset:", audioOffset);
    // console.log('audioToRemove:', audioToRemove)
    // console.log('audioToAdd:', audioToAdd)
    console.log("addToCuePosition:", cuePosition);

    const filenameExtensions = ".mov|.mp4|.m4v";
    const musicFileArrayBuffer =
      this.searchState.selectedTrack.audioArrayBuffer;
    const musicFilename = this.searchState.selectedTrack.music.filename.replace(
      / /g,
      "-",
    );
    const musicNewFilename =
      this.searchState.selectedTrack.music.filename.replace(/\.[^/.]+$/, "");

    let videoNewFilename = this.filename
      .replace(new RegExp(`\\b(${filenameExtensions})\\b`, "gi"), "-")
      .replaceAll(" ", "-")
      .replace(/\s{2,}/g, " ")
      .replace(/\.[^/.]+$/, "");

    videoNewFilename += `${musicNewFilename.replace(/ /g, "_")}${".mp4".replace(
      /[^a-zA-Z0-9.&\-]/gi,
      "-",
    )}`;

    console.log(videoNewFilename);
    let ffmpegPath = `../../../../../node_modules/@ffmpeg/`;
    // console.log(BASE_URL)
    if (BASE_URL.includes(`dev`)) {
      ffmpegPath = `/assets/ffmpeg/`;
    }
    if (BASE_URL.includes(`search`)) {
      ffmpegPath = `/assets/ffmpeg/`;
    }
    if (BASE_URL.includes(`localhost`)) {
      ffmpegPath = `/assets/ffmpeg/`;
    }

    this.searchState.logEvent(
      `video-sync-download`,
      "triggered",
      `${videoNewFilename} - ${musicNewFilename}`,
    );

    this.setExportProgress(0);
    let ffmpegWorker = createWorker({
      corePath: `${ffmpegPath}core/ffmpeg-core.js`,
      workerPath: `${ffmpegPath}dist/worker.min.js`,
      // logger: ({ message }) => console.log(message),
      progress: ({ ratio }) => {
        const exportMultiplier = 1;
        if (this.exportCount > 0) {
          // exportMultiplier = 2
        }
        const progress = ((ratio * 100.0) / exportMultiplier).toFixed(0);
        this.setExportProgress(progress);
        // console.log(`Complete: ${progress}%`)
      },
    });

    this.setExportText("preparing...");

    let shortest = ``;
    if (totalMusicDuration > this.videoDuration) {
      shortest = ` -shortest `;
    }
    const toCutOffEnd = 0.5;
    const amix = `amerge=inputs=2`;
    const fadeOutTime = (videoDuration - toCutOffEnd).toFixed(2);
    // console.log((0 + Math.abs(audioOffset)))
    console.log(fadeOutTime);
    const volumeFilter = `[0:a]volume=${this.videoAudioLevel.toFixed(
      2,
    )}[a1];[1:a]volume=${this.searchState.musicVolume.toFixed(2)}[a2];`;

    let filters = `-filter_complex ${volumeFilter}[a2]afade=out:st=${fadeOutTime}:d=${toCutOffEnd}[a5];[a5][a1]${amix}[a]`;

    if (audioToRemove !== 0) {
      const trimFromBeginning = `[a2]atrim=${audioToRemove}:200000,asetpts=PTS-STARTPTS[a4];`;
      filters = `-filter_complex ${volumeFilter}${trimFromBeginning}[a4]afade=out:st=${fadeOutTime}:d=${toCutOffEnd}[a5];[a5][a1]${amix}[a]`;
    }
    if (audioToAdd !== 0) {
      const addToBeginning = `aevalsrc=0:d=${audioToAdd}:s=44100:channel_layout=stereo[a3]`;
      filters = `-filter_complex ${addToBeginning};${volumeFilter}[a3][a2]concat=n=2:v=0:a=1[a4];[a4]afade=out:st=${fadeOutTime}:d=${toCutOffEnd}[a5];[a5][a1]${amix}[a]`;
    }

    // let filters = ` -filter_complex ${volumeFilter}[a2]afade=out:st=${fadeOutTime}:d=${toCutOffEnd}[a5];[a5][a1]${amix}[a]`
    // let filters = ` -filter_complex ${volumeFilter}[a2][a1]${amix}[a]`

    // -itsoffset ${audioOffset}

    // if (audioToAdd !== 0 && audioToRemove !== 0){
    //   const trimFromBeginning = `[a2]atrim=${audioToRemove}:200000,asetpts=PTS-STARTPTS[a4];`
    //   const addToBeginning = `aevalsrc=0:d=${audioToAdd}[a3];`
    //   filters = `${addToBeginning}${volumeFilter}${trimFromBeginning}[a3][a4]concat=n=2:v=0:a=1[a4];[a4]`
    // }
    // filters += `afade=out:st=${fadeOutTime}:d=${toCutOffEnd}[a5];[a5][a1]${amix}[a]`

    const cmd = `-i ${this.filename.replace(/[^a-zA-Z0-9.\-]/gi, "-")} \
      ${cuePos} -i ${musicFilename} \
      ${filters} \
      -map 0:v \
      -map [a] \
      -c:v copy \
      -c:a aac \
      -strict experimental \
      -b:a 192k \
      -ac 2 \
      ${shortest} \
      ${videoNewFilename}`;
    console.log(cmd);
    // return;

    console.log("Loading ffmpeg-core.js");
    await ffmpegWorker.load();
    console.log("Start transcoding");
    this.setExportText("encoding");
    await ffmpegWorker.write(
      this.filename.replace(/[^a-zA-Z0-9.\-]/gi, "-"),
      this.url,
    );
    await ffmpegWorker.write(musicFilename, musicFileArrayBuffer);
    await ffmpegWorker.run(cmd);
    console.log("Complete transcoding");
    const { data } = await ffmpegWorker.read(videoNewFilename);

    let videoUrl;
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }

    videoUrl = URL.createObjectURL(
      new Blob([data.buffer], { type: "video/mp4" }),
    );
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    a.href = videoUrl;
    a.download = videoNewFilename;
    a.click();

    this.setExportProgress(0);
    this.setExportText("export synced video");

    await ffmpegWorker.terminate();
    ffmpegWorker = null;
    const eCount = this.exportCount + 1;
    this.setExportCount(eCount);
    this.setExportingStatus(false);
    this.searchState.logEvent(
      `video-sync-download`,
      "finished",
      `${musicNewFilename}`,
    );
  };

  /**
   * @action
   * @param {string} url
   * @param {string} filename
   */
  setVideo = (url, filename) => {
    if (this.url !== url || this.filename !== filename) {
      // Reset all the saved audio offsets for a new video file
      this.audioOffset.clear();
    }
    // console.log(url);
    // console.log(filename);

    this.searchState.timeline.setVideoAnchor(0);
    this.searchState.timeline.reset();

    this.url = url;
    this.filename = filename;
  };

  resetVideo = () => {
    runInAction(() => {
      this.searchState.timeline.setVideoAnchor(0);
      this.searchState.timeline.reset();
      this.videoLoaded = false;

      this.url = null;
      this.filename = null;
    });
  };

  /**
   * @action
   * @param {HTMLVideoElement} videoRef
   */
  setVideoRef = (videoRef) => {
    runInAction(() => {
      this.videoRef = videoRef;
      this.videoLoaded = false;
    });
    if (videoRef == null) return;

    this.videoRef.volume = this.videoAudioLevel;

    // Add handlers for progress updates and notifying when loaded
    this.videoRef.addEventListener(
      "canplaythrough",
      () => {
        // console.log("canplaythrough");
      },
      false,
    );

    this.videoRef.addEventListener(
      "loadedmetadata",
      (event) => {
        if (event.target.videoHeight === 0) {
          // this is likely where a codec error happens - Chrome only supports h.264 files and lots of editors are now using h.265, which is patented
          this.searchState.showVideoSyncError();
          try {
            this.searchState.logEvent(
              `video-type-${this.filename}`,
              "error",
              `incorrect video codec`,
            );
          } catch (e) {}
          this.resetVideo();
          return;
        }
        console.log("loadedmetadata");
        runInAction(() => {
          this.videoLoaded = true;
        });
        this.videoDuration = this.videoRef.duration;
        this.searchState.triggerSyncTip();
        setTimeout(() => {
          if (this.waveformRef != null) {
            this.waveformRef.refreshResolution();
          }
        }, 300);
      },
      false,
    );
  };

  getVideoDuration = () => {
    let seconds = Math.round(this.videoDuration);
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;
    if (seconds < 10) {
      return `${minutes}:0${seconds}`;
    }

    return `${minutes}:${seconds}`;
  };

  /**
   * @observable
   * @param {number} value
   */
  convertSecondsToTimestamp = (value) => {
    let seconds = Math.round(value);
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;
    if (seconds < 10) {
      return `${minutes}:0${seconds}`;
    }

    return `${minutes}:${seconds}`;
  };

  getAudioDurationPercentage = (track) => {
    if (track.loadingAudio || !this.videoLoaded) return 1.0;
    return (
      this.searchState.timeline.trackDuration /
      this.searchState.timeline.videoDuration
    );
  };

  /**
   * @action
   * @param {number} level
   */
  setVideoAudioLevel = (level) => {
    this.videoAudioLevel = Math.min(Math.max(level, 0), 1);
    if (this.videoRef != null) this.videoRef.volume = this.videoAudioLevel;

    if (Object.keys(this.searchState.savedPlaylistData).length) {
      this.searchState.savedPlaylistData.videoVolume = this.videoAudioLevel;
    }
  };
}
